import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Mail, Loader2, Send, Link2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function GlobalEmailComposer({ open, onOpenChange }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    to: "", cc: "", bcc: "", subject: "", body: "",
  });
  const [linkedLead, setLinkedLead] = useState(null);
  const [leadQuery, setLeadQuery] = useState("");
  const [leadResults, setLeadResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({ to: "", cc: "", bcc: "", subject: "", body: "" });
      setLinkedLead(null);
      setLeadQuery("");
      setLeadResults([]);
      setShowResults(false);
    }
  }, [open]);

  // Debounced lead search
  useEffect(() => {
    if (!open) return;
    const q = leadQuery.trim();
    if (q.length === 0) {
      setLeadResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API}/email-composer/lead-search?q=${encodeURIComponent(q)}&limit=8`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setLeadResults(data.leads || []);
          setShowResults(true);
        }
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [leadQuery, open]);

  const parseList = (s) => (s || "").split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);

  const submit = async () => {
    const toList = parseList(form.to);
    if (toList.length === 0) {
      toast.error("Please enter at least one To: recipient");
      return;
    }
    const ccList = parseList(form.cc);
    const bccList = parseList(form.bcc);
    const invalid = [...toList, ...ccList, ...bccList].filter((e) => !EMAIL_RE.test(e));
    if (invalid.length) {
      toast.error(`Invalid email: ${invalid.join(", ")}`);
      return;
    }
    if (!form.subject.trim()) { toast.error("Subject is required"); return; }
    if (!form.body.trim()) { toast.error("Body is required"); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/email/send`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to_emails: toList,
          cc: ccList,
          bcc: bccList,
          subject: form.subject.trim(),
          body: form.body,
          lead_id: linkedLead?.lead_id || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to send email");
      const total = toList.length + ccList.length + bccList.length;
      toast.success(
        linkedLead
          ? `Email sent to ${total} recipient${total === 1 ? "" : "s"} · linked to ${linkedLead.business_name}`
          : `Email sent to ${total} recipient${total === 1 ? "" : "s"}`,
      );
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message, { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="global-email-composer"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            New Email
          </DialogTitle>
          <DialogDescription>
            Send any email from your CRM. Optionally link it to a lead's history.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Optional lead link */}
          <div>
            <Label className="flex items-center gap-2 text-sm">
              <Link2 className="w-3.5 h-3.5 text-slate-400" />
              Link to a lead <span className="text-xs text-slate-400 font-normal">(optional)</span>
            </Label>
            {linkedLead ? (
              <div className="mt-1 flex items-center gap-2 p-2 rounded-md bg-blue-50 border border-blue-200" data-testid="linked-lead-chip">
                <Badge className="bg-blue-100 text-blue-700">Linked</Badge>
                <span className="text-sm font-medium text-slate-900 truncate">{linkedLead.business_name}</span>
                {linkedLead.contact_name && (
                  <span className="text-xs text-slate-500 truncate">· {linkedLead.contact_name}</span>
                )}
                <button
                  type="button"
                  className="ml-auto text-slate-400 hover:text-red-600"
                  onClick={() => { setLinkedLead(null); setLeadQuery(""); }}
                  data-testid="unlink-lead-btn"
                  title="Unlink"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative mt-1">
                <Input
                  placeholder="Search business or contact name…"
                  value={leadQuery}
                  onChange={(e) => setLeadQuery(e.target.value)}
                  onFocus={() => setShowResults(leadResults.length > 0)}
                  onBlur={() => setTimeout(() => setShowResults(false), 200)}
                  data-testid="lead-search-input"
                />
                {searching && (
                  <Loader2 className="absolute right-2 top-2.5 w-4 h-4 text-slate-400 animate-spin" />
                )}
                {showResults && leadResults.length > 0 && (
                  <div
                    className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                    data-testid="lead-search-results"
                  >
                    {leadResults.map((l) => (
                      <button
                        type="button"
                        key={l.lead_id}
                        onClick={() => {
                          setLinkedLead(l);
                          // auto-fill To: with the lead's first email if user hasn't typed anything yet
                          const firstEmail = l.email || l.contact_email || l.contact2_email;
                          if (firstEmail && !form.to.trim()) {
                            setForm((f) => ({ ...f, to: firstEmail }));
                          }
                          setLeadQuery("");
                          setShowResults(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                        data-testid={`lead-search-result-${l.lead_id}`}
                      >
                        <div className="text-sm font-medium text-slate-900">{l.business_name}</div>
                        <div className="text-xs text-slate-500">
                          {[l.contact_name, l.city, l.state].filter(Boolean).join(" · ")}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="ge-to">To *</Label>
            <Input
              id="ge-to"
              placeholder="recipient@example.com, another@example.com"
              value={form.to}
              onChange={(e) => setForm({ ...form, to: e.target.value })}
              data-testid="ge-to"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ge-cc">Cc <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
              <Input
                id="ge-cc"
                placeholder="manager@example.com"
                value={form.cc}
                onChange={(e) => setForm({ ...form, cc: e.target.value })}
                data-testid="ge-cc"
              />
            </div>
            <div>
              <Label htmlFor="ge-bcc">Bcc <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
              <Input
                id="ge-bcc"
                placeholder="archive@example.com"
                value={form.bcc}
                onChange={(e) => setForm({ ...form, bcc: e.target.value })}
                data-testid="ge-bcc"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="ge-subject">Subject *</Label>
            <Input
              id="ge-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="ge-subject"
            />
          </div>

          <div>
            <Label htmlFor="ge-body">Body *</Label>
            <Textarea
              id="ge-body"
              rows={8}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Write your email here…"
              data-testid="ge-body"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="ge-send-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
